import { ImageProps } from 'next/image'

import Breadcrumb, { BreadcrumbProps } from '../../components/Breadcrumb'
import { Recipe } from '../../types/fam-graphql'
import { CoverSectionProps } from '../../components/CoverSection'
import { colors } from '../../theme'
import Router from '../../routes/router'
import routes from '../../routes/definitions'
import { selectors } from '../../redux'
import Tag from '../../components/Tag'
import Message from '../../components/Family/Message'

type RecipeCoverTransformerArgs = {
  recipe: Recipe | null
  t: any
  breadcrumbItems: BreadcrumbProps
  comesFromPlanning: boolean
  isFromHebdo?: boolean
  day?: number | string
  filters?: typeof selectors.recipes.filters
  isOffline?: boolean
  parentRecipeSlug?: string
}

const recipeCoverDefaultTransformer = (
  args: RecipeCoverTransformerArgs
): CoverSectionProps => {
  const {
    recipe,
    t,
    breadcrumbItems,
    comesFromPlanning,
    filters,
    day,
    isFromHebdo,
    parentRecipeSlug,
  } = args
  const earlierPlaningId = Router.router?.query?.earlierPlanningId
  return {
    title: recipe?.name ?? '',
    backLink: {
      ...(parentRecipeSlug
        ? Router.getRouteConfig(routes.recipe.name, { slug: parentRecipeSlug })
        : isFromHebdo
        ? Router.getRouteConfig(routes.hebdo.name)
        : earlierPlaningId
        ? Router.getRouteConfig(routes.earlierplanning.name, {
            id: earlierPlaningId,
            jour: day,
          })
        : !comesFromPlanning
        ? Router.getRouteConfig(routes.recipes.name, filters || {})
        : Router.getRouteConfig(routes.planning.name, { jour: day })),
      text: parentRecipeSlug
        ? t('recipe_back_to_parent')
        : isFromHebdo
        ? t('recipe_back_to_hebdo')
        : comesFromPlanning || earlierPlaningId
        ? t('recipe_back_to_my_menu')
        : t('recipe_back_to_list'),
      ...(!isFromHebdo &&
        !comesFromPlanning &&
        !earlierPlaningId && {
          icon: 'list',
        }),
    },
    backgroundColor: colors.redMandy,
    backgroundImageMobile: {
      alt: recipe?.cover_alt ?? 'Mobile cover background',
      src: recipe?.cover_mobile ?? '',
      layout: 'fill',
      objectFit: 'cover',
    } as ImageProps,
    backgroundImageDesktop: {
      alt: recipe?.cover_alt ?? 'Desktop cover background',
      src: recipe?.cover_desktop ?? '',
      layout: 'fill',
      objectFit: 'cover',
    } as ImageProps,
    breadcrumb: (
      <>
        {/* FIXME: Hardcoded */}
        <Breadcrumb
          hoverColor={colors.redMandy}
          links={breadcrumbItems.links}
        />
      </>
    ),
  }
}

const recipeCoverForFamilyTransformer = (
  args: RecipeCoverTransformerArgs
): CoverSectionProps => {
  const defaultRecipeCover = recipeCoverDefaultTransformer(args)

  const route = Router.getRouteConfig(routes.recipesByTag.name, {
    tag: 'à 4 mains',
  })

  return {
    ...defaultRecipeCover,
    showBreadcrumbInMobile: true,
    highlightProps: {
      text: defaultRecipeCover.title,
      backgroundColor: colors.familyYellow,
      color: colors.familyBurgundy,
    },
    backgroundColor: colors.redMandy,
    backgroundImageMobile: {
      alt: 'Mobile cover background',
      src: '/images/family/background_mobile.png',
      layout: 'fill',
      objectFit: 'cover',
      className: 'low-opacity',
    } as ImageProps,
    backgroundImageDesktop: {
      alt: 'Desktop cover background',
      src: '/images/family/background_desktop.png',
      layout: 'fill',
      objectFit: 'cover',
      className: 'low-opacity',
    } as ImageProps,
    breadcrumb: (
      <>
        <Tag
          textColor={colors.familyBlue}
          backgroundColor={colors.familyBlue}
          text="Les recettes à 4 mains"
          href={route.href}
          nextAs={route.nextAs}
        />
      </>
    ),
    ...(!args.recipe?.parent && {
      bottomContent: (
        <>
          <Message text={args.t('family_recipe_intro')} />
        </>
      ),
    }),
  }
}

export const recipeCoverTransformer = (args: RecipeCoverTransformerArgs) => {
  if (args.recipe?.family_recipe) {
    return recipeCoverForFamilyTransformer(args)
  } else {
    return recipeCoverDefaultTransformer(args)
  }
}
